/**
 * useTabStore — 멀티탭(MDI) 네비게이션 Zustand 스토어.
 *
 * 【역할】
 * - 사용자가 연 페이지들을 tab 으로 보관 (vue-spring-fw MDI 방식 UX).
 * - 첫 번째 탭은 항상 대시보드(닫기 불가)로 고정 — `ensureDashboardFirst()` 가 정규화.
 * - 현재 활성 탭 key 추적 + sessionStorage 영속화 (`fw.tab.tabs` / `fw.tab.active`).
 *
 * 【State】
 *   - openTabs: TabItem[] — 표시 순서 그대로의 탭 배열 (idx 0 항상 대시보드)
 *   - activeKey: string | null — 현재 활성 탭의 key (null = 미선택, 보통 DASHBOARD)
 *
 * 【Actions】
 *   - openTab(tab) — 새 탭 추가 또는 기존 탭 활성화
 *   - closeTab(key) — 탭 제거 + 다음 활성 탭 자동 선택. 반환: 새 active key
 *   - closeOthers(key) / closeRight(idx) / closeAll() — 우클릭 컨텍스트 메뉴 액션
 *   - setActive(key) — 활성 탭만 전환 (router.push 는 호출자 책임)
 *   - initTabs() — 마운트 시 1회 — 대시보드 prepend + active 보정
 *   - resetToDashboard() — 로그아웃 후처리 (sessionStorage 비움)
 *   - refreshLabels(findLabel) — 로케일 변경 시 모든 탭 라벨 재계산
 *
 * 【부수효과】
 *   - sessionStorage.setItem('fw.tab.tabs', ...) / 'fw.tab.active'
 *   - resetToDashboard() 는 위 두 키를 removeItem
 *
 * 【사용처】 TabBar / AppLayout / authStore.logout / i18n locale change.
 * 【포팅 원본】 vue-spring-fw `useTabStore.ts` + `TabBar.vue` (Pinia → Zustand 전환).
 */
import { create } from 'zustand'

/** 탭 한 건의 표시 정보. */
export interface TabItem {
  /** 식별자 — 보통 path 와 동일. 활성/제거 비교의 기준. */
  key: string
  /**
   * i18n 라벨 키 (cm_label.label_key) — TabBar render 시 `t(labelKey, label)` 로 현재 로케일 적용.
   * 메뉴 트리의 노드에 labelKey 가 있으면 항상 이걸 우선 사용 — 라벨 보존 로직 불필요.
   */
  labelKey?: string
  /** Fallback 표시 라벨 (i18n 번들 비어있을 때 또는 labelKey 미정의 시). */
  label: string
  /** 라우터 경로 — 클릭 시 router.push 대상. */
  path: string
  /** 닫기 버튼 노출 여부. 대시보드는 false 강제. */
  closable?: boolean
}

interface TabState {
  /** 화면 좌→우 순서대로의 열린 탭. idx 0 항상 대시보드. */
  openTabs: TabItem[]
  /** 현재 활성 탭의 key. null 이면 보통 DASHBOARD 로 폴백. */
  activeKey: string | null
  /** 새 탭 추가 또는 기존 탭으로 전환. */
  openTab: (tab: TabItem) => void
  /** 탭 제거 + 다음 활성 탭 결정. 대시보드는 닫기 불가. */
  closeTab: (key: string) => string | null
  /** 지정 탭과 대시보드만 남기고 모두 닫기. */
  closeOthers: (key: string) => void
  /** idx 우측의 모든 탭 닫기. */
  closeRight: (idx: number) => void
  /** 대시보드만 남기고 전부 닫기. */
  closeAll: () => string
  /** 활성 탭만 전환 (라우팅 별도). */
  setActive: (key: string) => void
  /** 앱 마운트 시 1회 — 대시보드 prepend + active 정합성 검사. */
  initTabs: () => void
  /** 로그아웃 후처리 — sessionStorage 비움 + 대시보드 단일 탭. */
  resetToDashboard: () => void
  /** 로케일 변경 시 모든 탭 label 재계산. */
  refreshLabels: (findLabel: (path: string) => string) => void
}

/** 대시보드 라우트 — 항상 첫 번째이자 닫을 수 없는 탭. */
export const DASHBOARD_KEY = '/dashboard'
/** 대시보드 탭 템플릿 — labelKey 우선, label 은 i18n 번들 미로드 시 fallback. */
export const DASHBOARD_TAB: TabItem = {
  key: DASHBOARD_KEY,
  labelKey: 'MNU_DASHBOARD',
  label: '대시보드',
  path: DASHBOARD_KEY,
  closable: false
}

const STORAGE_TABS = 'fw.tab.tabs'
const STORAGE_ACTIVE = 'fw.tab.active'

/**
 * sessionStorage 에서 탭 목록과 active key 를 복원.
 * 손상/미존재 시 대시보드 단일 탭으로 폴백 — 절대 throw 하지 않음.
 */
function loadFromStorage(): { openTabs: TabItem[]; activeKey: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_TABS)
    const active = sessionStorage.getItem(STORAGE_ACTIVE)
    const parsed: TabItem[] = raw ? JSON.parse(raw) : []
    const withDashboard = ensureDashboardFirst(parsed)
    const activeKey = active && withDashboard.some((t) => t.key === active) ? active : DASHBOARD_KEY
    return { openTabs: withDashboard, activeKey }
  } catch {
    return { openTabs: [{ ...DASHBOARD_TAB }], activeKey: DASHBOARD_KEY }
  }
}

/**
 * 현재 탭/active 를 sessionStorage 에 영속화. quota/private mode 실패는 silent.
 */
function saveToStorage(tabs: TabItem[], active: string | null) {
  try {
    sessionStorage.setItem(STORAGE_TABS, JSON.stringify(tabs))
    if (active) sessionStorage.setItem(STORAGE_ACTIVE, active)
    else sessionStorage.removeItem(STORAGE_ACTIVE)
  } catch {
    /* noop */
  }
}

/**
 * 대시보드 탭이 항상 idx 0 에 존재하고, 그 외는 closable=true 가 되도록 정규화.
 * (외부 입력(스토리지/액션)을 신뢰하지 않는 1차 방어선)
 *
 * 라벨 보존: 기존 입력에 대시보드 탭이 있으면 그 label 을 그대로 유지한다 (다국어 보존).
 * 없을 때만 DASHBOARD_TAB 폴백('대시보드')으로 신규 생성 — 이 경우 후속 refreshLabels 가 갱신.
 */
function ensureDashboardFirst(tabs: TabItem[]): TabItem[] {
  const others = tabs.filter((t) => t.key !== DASHBOARD_KEY)
  const existingDash = tabs.find((t) => t.key === DASHBOARD_KEY)
  const dashTab: TabItem = existingDash
    ? { ...existingDash, closable: false }
    : { ...DASHBOARD_TAB }
  return [dashTab, ...others.map((t) => ({ ...t, closable: true }))]
}

const initial = loadFromStorage()

export const useTabStore = create<TabState>((set, get) => ({
  openTabs: initial.openTabs,
  activeKey: initial.activeKey,

  initTabs: () => {
    const tabs = ensureDashboardFirst(get().openTabs)
    const active = tabs.some((t) => t.key === get().activeKey)
      ? (get().activeKey as string)
      : DASHBOARD_KEY
    set({ openTabs: tabs, activeKey: active })
    saveToStorage(tabs, active)
  },

  openTab: (tab) => {
    const { openTabs } = get()
    const isDashboard = tab.key === DASHBOARD_KEY
    const existing = openTabs.find((t) => t.key === tab.key)
    if (existing) {
      set({ activeKey: tab.key })
      saveToStorage(get().openTabs, tab.key)
      return
    }
    // 대시보드 신규 진입: 호출자가 전달한 label 우선(이미 다국어 적용된 값일 수 있음).
    // 없으면 DASHBOARD_TAB 폴백 — refreshLabels 가 후속 보정.
    const normalized: TabItem = isDashboard
      ? { ...DASHBOARD_TAB, label: tab.label || DASHBOARD_TAB.label }
      : { ...tab, closable: true }
    const next = isDashboard
      ? ensureDashboardFirst([normalized, ...openTabs])
      : ensureDashboardFirst([...openTabs, normalized])
    set({ openTabs: next, activeKey: tab.key })
    saveToStorage(next, tab.key)
  },

  closeTab: (key) => {
    if (key === DASHBOARD_KEY) return null
    const { openTabs, activeKey } = get()
    const idx = openTabs.findIndex((t) => t.key === key)
    if (idx < 0) return null
    const target = openTabs[idx]
    if (target.closable === false) return null
    const next = openTabs.filter((t) => t.key !== key)
    const safeNext = ensureDashboardFirst(next)
    let nextActive = activeKey
    if (activeKey === key) {
      nextActive = safeNext[idx]?.key ?? safeNext[idx - 1]?.key ?? DASHBOARD_KEY
    }
    set({ openTabs: safeNext, activeKey: nextActive })
    saveToStorage(safeNext, nextActive)
    return nextActive
  },

  closeOthers: (key) => {
    const { openTabs } = get()
    const target = openTabs.find((t) => t.key === key)
    const existingDash = openTabs.find((t) => t.key === DASHBOARD_KEY)
    const dashTab: TabItem = existingDash
      ? { ...existingDash, closable: false }
      : { ...DASHBOARD_TAB }
    const kept: TabItem[] = [dashTab]
    if (target && target.key !== DASHBOARD_KEY) kept.push({ ...target, closable: true })
    set({ openTabs: kept, activeKey: key })
    saveToStorage(kept, key)
  },

  closeRight: (idx: number) => {
    const { openTabs } = get()
    const kept = openTabs.slice(0, idx + 1)
    const safeKept = ensureDashboardFirst(kept)
    const active = safeKept[idx]?.key ?? DASHBOARD_KEY
    set({ openTabs: safeKept, activeKey: active })
    saveToStorage(safeKept, active)
  },

  closeAll: () => {
    const existingDash = get().openTabs.find((t) => t.key === DASHBOARD_KEY)
    const dashTab: TabItem = existingDash
      ? { ...existingDash, closable: false }
      : { ...DASHBOARD_TAB }
    const only = [dashTab]
    set({ openTabs: only, activeKey: DASHBOARD_KEY })
    saveToStorage(only, DASHBOARD_KEY)
    return DASHBOARD_KEY
  },

  setActive: (key) => {
    set({ activeKey: key })
    saveToStorage(get().openTabs, key)
  },

  resetToDashboard: () => {
    const only = [{ ...DASHBOARD_TAB }]
    set({ openTabs: only, activeKey: DASHBOARD_KEY })
    try {
      sessionStorage.removeItem(STORAGE_TABS)
      sessionStorage.removeItem(STORAGE_ACTIVE)
    } catch {
      /* noop */
    }
  },

  refreshLabels: (findLabel: (path: string) => string) =>
    set((s) => {
      // W19-B 이전: DASHBOARD 는 '대시보드' 하드코딩이라 locale 전환 시 영어로 안 바뀜.
      //             이제 메뉴 트리에서 라벨을 찾으면 대시보드도 갱신되도록 허용.
      const updated = s.openTabs.map((t) => ({
        ...t,
        label: findLabel(t.path) || t.label
      }))
      saveToStorage(updated, s.activeKey)
      return { openTabs: updated }
    })
}))
